import React from 'react';
import { useTranslation } from 'react-i18next';
import { Columns2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { CADModelViewer } from '../../components/cad';
import { CADComparisonSummary, CADMarker, CADModel } from '../../types/cad';

interface CadViewerStageProps {
  viewerMode: 'single' | 'compare';
  glbUrl: string | null;
  comparisonGlbUrl: string | null;
  selectedModel: CADModel | null;
  comparisonModel: CADModel | null;
  comparisonSummary: CADComparisonSummary | null;
  mergedMarkers: CADMarker[];
  chatMarkers: CADMarker[];
  onMarkerClick: (marker: CADMarker) => void;
  onExitComparison: () => void;
}

/**
 * Center column: either the single 3D viewer or the side-by-side comparison
 * stage with its confidence header. Markup is unchanged from the inline block
 * it replaced.
 */
const CadViewerStage: React.FC<CadViewerStageProps> = ({
  viewerMode,
  glbUrl,
  comparisonGlbUrl,
  selectedModel,
  comparisonModel,
  comparisonSummary,
  mergedMarkers,
  chatMarkers,
  onMarkerClick,
  onExitComparison,
}) => {
  const { t } = useTranslation();

  if (viewerMode === 'single') {
    return (
      <div className="min-w-0 flex-1 overflow-hidden rounded-lg border border-border bg-card">
        <CADModelViewer
          glbUrl={glbUrl}
          filename={selectedModel?.filename}
          markers={mergedMarkers}
          onMarkerClick={onMarkerClick}
        />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      {/* Comparison header */}
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-1">
        <Columns2 className="size-4 text-primary" />
        <span className="text-sm font-semibold">{t('cad.comparisonMode')}</span>
        <Badge
          variant={
            comparisonSummary?.confidence === 'high'
              ? 'success'
              : comparisonSummary?.confidence === 'medium'
                ? 'warning'
                : 'destructive'
          }
        >
          {t('cad.confidenceLabel', {
            value: comparisonSummary?.confidence?.toUpperCase() || '-',
          })}
        </Badge>
        <div className="flex-1" />
        <TooltipIconButton
          label={t('cad.exitComparisonMode')}
          icon={<X />}
          size="icon-sm"
          onClick={onExitComparison}
        />
      </div>
      {/* Side-by-side viewers */}
      <div className="flex min-h-0 flex-1 gap-2">
        <div className="relative min-w-0 flex-1 overflow-hidden rounded-lg border border-border bg-card">
          <span className="absolute left-2 top-1 z-[1] rounded bg-background px-1 text-xs font-semibold">
            A: {selectedModel?.filename}
          </span>
          <CADModelViewer glbUrl={glbUrl} filename={selectedModel?.filename} markers={chatMarkers.filter(m => !m.side || m.side === 'A')} />
        </div>
        <div className="relative min-w-0 flex-1 overflow-hidden rounded-lg border border-border bg-card">
          <span className="absolute left-2 top-1 z-[1] rounded bg-background px-1 text-xs font-semibold">
            B: {comparisonModel?.filename}
          </span>
          <CADModelViewer glbUrl={comparisonGlbUrl} filename={comparisonModel?.filename} markers={chatMarkers.filter(m => m.side === 'B')} />
        </div>
      </div>
    </div>
  );
};

export default CadViewerStage;
